/**
 * On-screen kiosk keyboard (owner 15.10.2026): our own design instead of the iPad keyboard —
 * one simple layout, no layout switching, no special characters beyond what a field needs.
 *
 *  - `text`   names and wishes: letters, space, - ' . and a key for accented letters (á ç ã …).
 *             Each word starts with a capital letter automatically.
 *  - `email`  letters, digits, @ . - _ and shortcuts (@gmail.com …, .com, .pt).
 *  - `digits` phone numbers: a big 0–9 pad.
 *
 * Pure functions only; the view is in Keyboard.tsx.
 */
export type KeyboardMode = 'text' | 'email' | 'digits';

export const DIGIT_ROW = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
export const LETTER_ROWS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];
/** Letters used in Portuguese, Spanish, French and German names. */
export const ACCENT_ROWS = [
  ['á', 'à', 'â', 'ã', 'é', 'è', 'ê', 'í', 'ó', 'ô'],
  ['õ', 'ú', 'ü', 'ç', 'ñ', 'ö', 'ä', 'ï', 'ë'],
];
export const EMAIL_SYMBOLS = ['@', '.', '-', '_'];
export const TEXT_SYMBOLS = ['-', "'", '.'];
export const EMAIL_DOMAINS = ['@gmail.com', '@hotmail.com', '@outlook.com', '@icloud.com', '@sapo.pt'];
export const EMAIL_ENDINGS = ['.com', '.pt'];

export type KeyAction = { type: 'char'; value: string } | { type: 'backspace' } | { type: 'clear' };

/** New field value after a key; `max` = the field's maxLength. */
export function applyKey(value: string, action: KeyAction, mode: KeyboardMode, shift: boolean, max = 120): string {
  switch (action.type) {
    case 'backspace':
      return value.slice(0, -1);
    case 'clear':
      return '';
    case 'char': {
      let ch = action.value;
      if (mode === 'digits' && !/^\d$/.test(ch)) return value;
      if (mode === 'email') {
        ch = ch.toLowerCase();
        // One @ only, never a space.
        if (ch.includes('@') && value.includes('@')) ch = ch.replace('@', '');
        if (ch === ' ' || ch === '') return value;
      }
      if (mode === 'text') {
        if (ch === ' ' && (value === '' || value.endsWith(' '))) return value; // no double spaces
        if (ch.length === 1 && /\p{L}/u.test(ch) && (shift || startsWord(value))) ch = ch.toUpperCase();
      }
      return (value + ch).slice(0, max);
    }
  }
}

/** Text fields capitalise the first letter of every word (names: "Maria Santos"). */
export function startsWord(value: string): boolean {
  return value === '' || /[\s'-]$/.test(value);
}

/** E-mail shortcuts that make sense right now: domains before the @, endings after it. */
export function emailShortcuts(value: string): string[] {
  if (!value || value.endsWith('@')) return value ? EMAIL_DOMAINS.map((d) => d.slice(1)) : [];
  if (!value.includes('@')) return EMAIL_DOMAINS;
  const domain = value.slice(value.indexOf('@') + 1);
  return domain && !domain.includes('.') ? EMAIL_ENDINGS : [];
}
